/* RecipeVisionUpload — качване на снимка на ястие → Gemini 2.5 Pro парсва съставки
 * → потребителят confirm-ва/редактира → /meals/add-batch.
 *
 * Props:
 *  - variant: "meal-log" (primary CTA бутон в дневника) | "meal-planner" (inline chip)
 *  - selectedMealType: preset meal type (опционално)
 *  - onAdded: callback след успешно добавяне
 */
import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Upload, Loader2, X, Check, ImageIcon, Link as LinkIcon } from "lucide-react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import CreditBadge from "@/components/CreditBadge";  // iter1118

const MAX_FILE_BYTES = 10 * 1024 * 1024;

export default function RecipeVisionUpload({ variant = "meal-log", selectedMealType = "", onAdded }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const fileRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | uploading | preview | error | url_prompt
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);
  const [editable, setEditable] = useState([]);
  const [mealTypeChoice, setMealTypeChoice] = useState(selectedMealType || "lunch");
  const [submitting, setSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  // iter361 — URL import state
  const [urlInput, setUrlInput] = useState("");
  const [urlSubmitting, setUrlSubmitting] = useState(false);

  // Live recalculation: when user un-checks an ingredient or adjusts amount_g,
  // recompute calories/DIL proportionally so the header reflects the current
  // selection (not the AI's original estimate). `totalAmountG` of the original
  // recipe = sum of all suggested amounts; `keptAmountG` = sum of currently
  // kept ingredients × their (possibly edited) amount_g.
  const totalAmountG = React.useMemo(() => {
    return (result?.ingredients || []).reduce((sum, f) => sum + (Number(f.amount_g) || 0), 0);
  }, [result]);

  const keptAmountG = React.useMemo(() => {
    return editable.filter(f => f._keep).reduce((sum, f) => sum + (Math.max(0, Number(f.amount_g) || 0)), 0);
  }, [editable]);

  const liveScale = totalAmountG > 0 ? keptAmountG / totalAmountG : 0;
  const liveCalories = Math.round((result?.estimated_calories || 0) * liveScale);
  const liveDil = Math.round((result?.estimated_dil || 0) * liveScale * 10) / 10;

  const reset = () => {
    setState("idle");
    setErrorMsg("");
    setResult(null);
    setEditable([]);
    setSubmitting(false);
    setUrlInput("");
    setUrlSubmitting(false);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const openPicker = () => fileRef.current?.click();
  const openUrlPrompt = () => { setUrlInput(""); setErrorMsg(""); setState("url_prompt"); };

  // iter361 — import recipe from a URL (BBC, NYT, AllRecipes, gotvach.bg, etc.)
  const submitUrlImport = async () => {
    const url = (urlInput || "").trim();
    if (!url || !/^https?:\/\//i.test(url)) {
      toast.error(bg ? "Въведете валиден https:// URL." : "Enter a valid https:// URL.");
      return;
    }
    setUrlSubmitting(true);
    setErrorMsg("");
    try {
      const { data } = await axios.post(`${API}/recipe/url-import`, {
        url,
        language: bg ? "bg" : "en",
        meal_type: selectedMealType || undefined,
      });
      if (!data?.ingredients?.length) {
        setErrorMsg(bg ? "Не беше разпозната рецепта от адреса. Опитайте с друга страница." : "No recipe recognized from URL. Try another site.");
        setState("error");
        return;
      }
      setResult(data);
      setEditable(data.ingredients.map((f) => ({ ...f, _keep: true })));
      setMealTypeChoice(data.meal_type || selectedMealType || "lunch");
      // Show site's hero image as preview if present.
      if (data.image_url) setPreviewUrl(data.image_url);
      setState("preview");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      setErrorMsg(bg ? `Грешка: ${detail}` : `Error: ${detail}`);
      setState("error");
    } finally {
      setUrlSubmitting(false);
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error(bg ? "Файлът е твърде голям (макс. 10 MB)." : "File too large (max 10 MB).");
      return;
    }
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes((file.type || "").toLowerCase())) {
      toast.error(bg ? "Разрешени формати: JPEG, PNG, WEBP." : "Allowed formats: JPEG, PNG, WEBP.");
      return;
    }
    setPreviewUrl(URL.createObjectURL(file));
    setState("uploading");
    setErrorMsg("");
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("language", bg ? "bg" : "en");
      if (selectedMealType) form.append("meal_type", selectedMealType);
      const { data } = await axios.post(`${API}/recipe/vision-parse`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data?.ingredients?.length) {
        setErrorMsg(bg ? "Не беше разпозната рецепта. Опитайте с по-ясна снимка." : "No recipe recognized. Try a clearer photo.");
        setState("error");
        return;
      }
      setResult(data);
      setEditable(data.ingredients.map((f) => ({ ...f, _keep: true })));
      setMealTypeChoice(data.meal_type || selectedMealType || "lunch");
      setState("preview");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      setErrorMsg(bg ? `Грешка: ${detail}` : `Error: ${detail}`);
      setState("error");
    }
  };

  const confirmAdd = async () => {
    const keep = editable.filter(f => f._keep && f.name?.trim());
    if (!keep.length) {
      toast.error(bg ? "Няма избрани съставки." : "No ingredients selected.");
      return;
    }
    setSubmitting(true);
    let ok = 0;
    const failed = [];
    try {
      // Use /meals/add per item to get automatic USDA fuzzy enrichment (calories,
      // protein, carbs, fat, fiber, minerals). /meals/add-batch expects pre-computed
      // macros which we don't have from a Vision result.
      for (const f of keep) {
        const payload = {
          food_name: f.name.trim(),
          amount_g: Math.max(1, Math.min(2000, Number(f.amount_g) || 100)),
          meal_type: mealTypeChoice,
          calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, saturated_fat: 0,
          source: "recipe_vision",
        };
        try {
          await axios.post(`${API}/meals/add`, payload);
          ok += 1;
        } catch (err) {
          failed.push(f.name);
        }
      }
      if (ok > 0) {
        toast.success(
          bg
            ? `Добавена рецепта „${result.meal_name_bg}" (${ok} съставки${failed.length ? ` · ${failed.length} неуспешно` : ""})`
            : `Added recipe "${result.meal_name_bg}" (${ok} ingredients${failed.length ? ` · ${failed.length} failed` : ""})`
        );
        window.dispatchEvent(new Event("meal-added"));
        window.dispatchEvent(new Event("meal-log-changed"));
        if (typeof onAdded === "function") onAdded(result, keep);
        reset();
      } else {
        toast.error(bg ? "Неуспешно добавяне. Опитайте отново." : "Failed to add. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // iter358 — save the AI-recognised recipe to user's "Saved recipes" so it
  // can be re-logged in 1 click later (no need to retake the photo).
  const saveToFavorites = async () => {
    const keep = editable.filter(f => f._keep && f.name?.trim());
    if (!keep.length) {
      toast.error(bg ? "Няма избрани съставки." : "No ingredients selected.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/recipes/favorite`, {
        name: result?.meal_name_bg || result?.meal_name_en || "Рецепта",
        meal_type: mealTypeChoice,
        ingredients: keep.map(f => ({
          name: f.name.trim(),
          amount_g: Math.max(1, Math.min(2000, Number(f.amount_g) || 100)),
        })),
        image_url: result?.image_url || null,
      });
      // iter593 — Toast с линк към Recipe Vault (потребителят знае къде да я намери)
      toast.success(
        bg ? "Добавена в Готварската книга ✓" : "Added to Recipe Vault ✓",
        {
          duration: 5000,
          action: {
            label: bg ? "Виж" : "View",
            onClick: () => { try { window.location.href = "/dashboard/recipes"; } catch (_) { /* ignore */ } },
          },
        }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при запис" : "Save failed"));
    } finally {
      setSubmitting(false);
    }
  };

  // iter358 — persist as a composite item in the personal food library so
  // it appears in food autocomplete next time the user types its name.
  const saveToPersonalDB = async () => {
    const keep = editable.filter(f => f._keep && f.name?.trim());
    if (!keep.length) {
      toast.error(bg ? "Няма избрани съставки." : "No ingredients selected.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/recipes/save-to-db`, {
        name: result?.meal_name_bg || result?.meal_name_en || "Рецепта",
        ingredients: keep.map(f => ({
          name: f.name.trim(),
          amount_g: Math.max(1, Math.min(2000, Number(f.amount_g) || 100)),
        })),
        image_url: result?.image_url || null,
      });
      toast.success(bg ? "Запазено в моите храни" : "Saved to my foods");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при запис" : "Save failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const triggerClasses = variant === "meal-planner"
    ? `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors
        ${isDark ? "bg-fuchsia-500/15 text-fuchsia-300 hover:bg-fuchsia-500/25 border border-fuchsia-500/30"
                 : "bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100 border border-fuchsia-200"}`
    : variant === "plate-simulator"
    ? `inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-lg text-white shadow hover:shadow-md transition-all bg-gradient-to-r from-fuchsia-500 to-purple-500`
    : `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors
        ${isDark ? "bg-fuchsia-500/15 text-fuchsia-300 hover:bg-fuchsia-500/25 border border-fuchsia-500/30"
                 : "bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100 border border-fuchsia-200"}`;

  return (
    <>
      {/* Trigger */}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={onFileChosen}
        data-testid="recipe-vision-file-input"
      />
      <button
        onClick={openPicker}
        disabled={state === "uploading"}
        className={triggerClasses}
        data-testid={`recipe-vision-trigger-${variant}`}
        title={bg ? "Разпознай рецепта от снимка (AI, 1 кредит)" : "Recognize recipe from photo (AI, 1 credit)"}
      >
        {state === "uploading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
        <span>{bg ? "Снимка → рецепта" : "Photo → recipe"}</span>
        <CreditBadge featureId="recipe_vision" fallbackCost={1} variant="inline" showLabel={false} className="ml-1" />
      </button>
      {/* iter361 — URL recipe import trigger (next to the camera trigger) */}
      <button
        onClick={openUrlPrompt}
        disabled={state === "uploading" || state === "url_prompt"}
        className={triggerClasses}
        data-testid={`recipe-url-trigger-${variant}`}
        title={bg ? "Внесете рецепта по адрес (AllRecipes, BBC, gotvach.bg, NYT, …) — 1 кредит" : "Import recipe from URL (AllRecipes, BBC, NYT, …) — 1 credit"}
        style={{ marginLeft: 6 }}
      >
        <LinkIcon className="w-3.5 h-3.5" />
        <span>{bg ? "URL → рецепта" : "URL → recipe"}</span>
        <CreditBadge featureId="recipe_url_import" fallbackCost={1} variant="inline" showLabel={false} className="ml-1" />
      </button>

      {/* iter361 — URL prompt modal */}
      {state === "url_prompt" && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && reset()}
          data-testid="recipe-url-prompt"
        >
          <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden
            ${isDark ? "bg-[hsl(222,47%,10%)] text-gray-100" : "bg-white text-gray-900"}`}>
            <div className={`px-4 py-3 flex items-center justify-between border-b
              ${isDark ? "border-white/10" : "border-gray-100"}`}>
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-fuchsia-500" />
                <span className="text-sm font-semibold">
                  {bg ? "Внасяне по адрес на рецепта" : "Import from recipe URL"}
                </span>
              </div>
              <button onClick={reset} className="opacity-60 hover:opacity-100" data-testid="recipe-url-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <p className={`text-[11px] leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg
                  ? "Поддържат се 580+ международни сайта (AllRecipes, BBC Good Food, NYT Cooking, Bon Appétit, …). За BG сайтове като gotvach.bg, supichka.com — използва се AI парсер. Струва 1 кредит."
                  : "Supports 580+ recipe sites (AllRecipes, BBC Good Food, NYT Cooking, Bon Appétit, …). For other sites we use AI to parse the article text. Costs 1 credit."}
              </p>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://cooking.nytimes.com/recipes/..."
                className={`w-full px-3 py-2 rounded-lg text-[13px] border focus:outline-none focus:ring-2 focus:ring-fuchsia-500
                  ${isDark ? "bg-white/5 border-white/10 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                data-testid="recipe-url-input"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && !urlSubmitting) submitUrlImport(); }}
              />
              {errorMsg && (
                <div className={`text-[11px] rounded-lg p-2 leading-relaxed
                  ${isDark ? "bg-amber-500/15 border border-amber-500/40 text-amber-200" : "bg-amber-50 border border-amber-200 text-amber-900"}`}>
                  {errorMsg}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={reset} data-testid="recipe-url-cancel">
                  {bg ? "Отказ" : "Cancel"}
                </Button>
                <Button
                  onClick={submitUrlImport}
                  disabled={urlSubmitting || !urlInput.trim()}
                  className="bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 text-white"
                  data-testid="recipe-url-submit"
                >
                  {urlSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <LinkIcon className="w-4 h-4 mr-1" />}
                  {bg ? "Внесете (1 кредит)" : "Import (1 cr.)"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error inline panel */}
      {state === "error" && errorMsg && (
        <div
          data-testid="recipe-vision-error"
          className={`mt-2 text-[11px] rounded-lg p-2 leading-relaxed
            ${isDark ? "bg-amber-500/15 border border-amber-500/40 text-amber-200" : "bg-amber-50 border border-amber-200 text-amber-900"}`}
        >
          <div className="flex items-start justify-between gap-2">
            <span>{errorMsg}</span>
            <button onClick={reset} className="shrink-0 opacity-70 hover:opacity-100">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {state === "preview" && result && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && reset()}
          data-testid="recipe-vision-preview"
        >
          <div className={`w-full sm:max-w-lg max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden
            ${isDark ? "bg-[hsl(222,47%,10%)] text-gray-100" : "bg-white text-gray-900"}`}>
            {/* Header (sticky top) */}
            <div className={`shrink-0 px-4 py-3 flex items-center justify-between border-b
              ${isDark ? "border-white/10" : "border-gray-100"}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Upload className="w-4 h-4 text-fuchsia-500 shrink-0" />
                <div className="leading-tight min-w-0">
                  <div className="text-sm font-semibold truncate">{result.meal_name_bg || (bg ? "Разпозната рецепта" : "Recognized recipe")}</div>
                  <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`} data-testid="recipe-live-totals">
                    {bg ? "увереност" : "confidence"}: {result.confidence || "medium"}
                    {liveDil > 0 && ` · DIL ~${liveDil}`}
                    {liveCalories > 0 && ` · ~${liveCalories} kcal`}
                    {keptAmountG > 0 && ` · ${keptAmountG}g`}
                  </div>
                </div>
              </div>
              <button onClick={reset} data-testid="recipe-vision-close" className="opacity-60 hover:opacity-100 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body (scrollable — fills remaining space between header & footer) */}
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
              {previewUrl && (
                <div className={`rounded-lg overflow-hidden border ${isDark ? "border-white/10" : "border-gray-200"}`}>
                  <img src={previewUrl} alt="recipe" className="w-full max-h-48 object-cover" />
                </div>
              )}
              {result.notes && (
                <div className={`text-[11px] italic leading-relaxed rounded-lg p-2
                  ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-50 text-gray-600"}`}>
                  {result.notes}
                </div>
              )}

              {/* Meal type */}
              <div className="flex flex-wrap gap-1.5">
                {["breakfast", "lunch", "dinner", "snack"].map((mt) => (
                  <button
                    key={mt}
                    onClick={() => setMealTypeChoice(mt)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                      mealTypeChoice === mt
                        ? "bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white"
                        : isDark ? "bg-white/5 text-gray-300 hover:bg-white/10" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    data-testid={`recipe-meal-type-${mt}`}
                  >
                    {bg
                      ? { breakfast: "Закуска", lunch: "Обяд", dinner: "Вечеря", snack: "Междинно" }[mt]
                      : mt.charAt(0).toUpperCase() + mt.slice(1)}
                  </button>
                ))}
              </div>

              {/* Ingredients list */}
              <div className="space-y-1.5">
                {editable.map((f, i) => (
                  <div
                    key={`ing-${i}`}
                    data-testid={`recipe-ingredient-row-${i}`}
                    className={`flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors ${
                      f._keep
                        ? isDark ? "bg-fuchsia-500/10 border border-fuchsia-500/30" : "bg-fuchsia-50 border border-fuchsia-200"
                        : isDark ? "bg-white/5 border border-white/10 opacity-50" : "bg-gray-50 border border-gray-200 opacity-60"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!f._keep}
                      onChange={(e) => {
                        const arr = [...editable];
                        arr[i] = { ...arr[i], _keep: e.target.checked };
                        setEditable(arr);
                      }}
                      className="w-4 h-4 accent-fuchsia-500"
                      data-testid={`recipe-ing-keep-${i}`}
                      title={bg ? "Включете или изключете при записването" : "Include/exclude when logging"}
                    />
                    <input
                      type="text"
                      value={f.name}
                      onChange={(e) => {
                        const arr = [...editable];
                        arr[i] = { ...arr[i], name: e.target.value };
                        setEditable(arr);
                      }}
                      className={`flex-1 min-w-0 text-[12px] bg-transparent border-b focus:outline-none
                        ${isDark ? "border-white/10 focus:border-fuchsia-500" : "border-gray-200 focus:border-fuchsia-500"}`}
                      data-testid={`recipe-ing-name-${i}`}
                    />
                    <input
                      type="number"
                      min="1"
                      max="2000"
                      value={f.amount_g}
                      onChange={(e) => {
                        const arr = [...editable];
                        arr[i] = { ...arr[i], amount_g: Number(e.target.value) };
                        setEditable(arr);
                      }}
                      className={`w-16 text-[12px] text-right bg-transparent border-b focus:outline-none
                        ${isDark ? "border-white/10 focus:border-fuchsia-500" : "border-gray-200 focus:border-fuchsia-500"}`}
                      data-testid={`recipe-ing-amount-${i}`}
                    />
                    <span className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>g</span>
                    {/* iter593 — Hard delete button (premahane от списъка изцяло) */}
                    <button
                      type="button"
                      onClick={() => {
                        const arr = editable.filter((_, idx) => idx !== i);
                        setEditable(arr);
                      }}
                      className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors
                        ${isDark
                          ? "bg-red-500/15 hover:bg-red-500/30 text-red-300"
                          : "bg-red-50 hover:bg-red-100 text-red-600 border border-red-200"}`}
                      title={bg ? "Премахни напълно от списъка" : "Remove from list entirely"}
                      data-testid={`recipe-ing-remove-${i}`}
                      aria-label="remove ingredient"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer (sticky bottom — always visible) */}
            <div className={`shrink-0 px-3 py-3 flex flex-wrap items-center justify-between gap-2 border-t
              ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-gray-50"}`}>
              <Button variant="ghost" onClick={reset} data-testid="recipe-vision-cancel">
                {bg ? "Отказ" : "Cancel"}
              </Button>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={saveToFavorites}
                  disabled={submitting}
                  className="border-amber-400 text-amber-600 hover:bg-amber-50 dark:text-amber-300"
                  data-testid="recipe-vision-save-favorite"
                  title={bg ? "Запазване в готварската книга за бърз повторен запис и поправка" : "Save to Recipe Vault for quick re-log and editing"}
                >
                  {bg ? "📖 В Готварската книга" : "📖 To Recipe Vault"}
                </Button>
                <Button
                  variant="outline"
                  onClick={saveToPersonalDB}
                  disabled={submitting}
                  className="border-indigo-400 text-indigo-600 hover:bg-indigo-50 dark:text-indigo-300"
                  data-testid="recipe-vision-save-db"
                  title={bg ? "Запазване в моите храни (после се намира при търсене)" : "Save to my foods (searchable in autocomplete)"}
                >
                  {bg ? "💾 В моите храни" : "💾 My foods"}
                </Button>
                <Button
                  onClick={confirmAdd}
                  disabled={submitting}
                  className="bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 text-white"
                  data-testid="recipe-vision-confirm"
                  title={bg ? "Добавяне на съставките в хранителния дневник за днес" : "Add ingredients to today's food diary"}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  {bg ? "В дневника" : "To diary"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
